import { createManualTrade, type ManualTradeInput } from './manualTradeApi';
import { getPrivateTradingScopeGeneration, getVerifiedPrivateTradingScope, readPrivateTradingValue, removePrivateTradingValue, writePrivateTradingValue } from '../storage/privateTradingStorage';

const DRAFT_DOMAIN = 'journal-pending-manual-trade';

export type JournalManualTradeDraft = { input: ManualTradeInput };

export function readJournalManualTradeDraft(): JournalManualTradeDraft | null {
  const draft = readPrivateTradingValue<JournalManualTradeDraft>(DRAFT_DOMAIN);
  const input = draft?.input;
  return input && typeof input.accountId === 'string' && typeof input.idempotencyKey === 'string'
    ? draft : null;
}

export async function saveJournalManualTrade(
  payload: Omit<ManualTradeInput, 'idempotencyKey'>,
): Promise<{ tradeId: string; accountName: string; created: boolean }> {
  const pending = readJournalManualTradeDraft();
  if (pending) {
    const previous: Partial<ManualTradeInput> = { ...pending.input };
    delete previous.idempotencyKey;
    if (JSON.stringify(previous) !== JSON.stringify(payload)) {
      throw new Error('A previous Journal trade is still pending. Retry that saved draft before creating another trade.');
    }
    return retryJournalManualTrade();
  }

  const idempotencyKey = globalThis.crypto?.randomUUID?.();
  if (!idempotencyKey) throw new Error('This browser could not create a secure trade ID. Your form remains open; please try again.');
  const input = { ...payload, idempotencyKey };
  if (!writePrivateTradingValue(DRAFT_DOMAIN, { input })) {
    throw new Error('The Journal trade draft could not be saved locally. Your form remains open; please try again.');
  }
  return retryJournalManualTrade();
}

export async function retryJournalManualTrade(): Promise<{ tradeId: string; accountName: string; created: boolean }> {
  const pending = readJournalManualTradeDraft();
  if (!pending) throw new Error('There is no pending Journal trade to retry.');
  const scope = getVerifiedPrivateTradingScope();
  const generation = getPrivateTradingScopeGeneration();
  const result = await createManualTrade(pending.input);
  const currentScope = getVerifiedPrivateTradingScope();
  if (!scope || generation !== getPrivateTradingScopeGeneration()
    || scope.tenantId !== currentScope?.tenantId || scope.userId !== currentScope?.userId) {
    throw new Error('The signed-in account changed while saving. Sign back in to the original account to verify and retry its draft.');
  }
  // Keep the draft if local removal fails: a later retry is safe because the
  // same idempotency key is reused and the server confirms the existing row.
  removePrivateTradingValue(DRAFT_DOMAIN);
  return result;
}
